// Workshop Detail page for KATA Dashboard

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { useWorkshopStore, useCurrentWorkshop } from '../stores/workshopStore';
import { WorkshopService } from '../services/workshop';
import { UserService, User } from '../services/user';
import { useIsAdmin } from '../stores/authStore';
import Alert from '../components/common/Alert';

const WorkshopDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchWorkshop, updateProgress, clearCurrentWorkshop } = useWorkshopStore();
  const { workshop, isLoading, error } = useCurrentWorkshop();
  const isAdmin = useIsAdmin();
  
  // Get return URL from query parameters
  const returnUrl = searchParams.get('returnTo') || '/analytics';
  
  const [isUpdatingProgress, setIsUpdatingProgress] = useState(false);
  const [progressInput, setProgressInput] = useState('');
  const [showProgressUpdate, setShowProgressUpdate] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [deleteAlert, setDeleteAlert] = useState<{
    isOpen: boolean;
    workshopId: number | null;
    workshopTitle: string;
  }>({
    isOpen: false,
    workshopId: null,
    workshopTitle: ''
  });

  useEffect(() => {
    if (id) {
      fetchWorkshop(parseInt(id));
      loadUsers(); // Load users when component mounts
    }
    
    return () => {
      clearCurrentWorkshop();
    };
  }, [id, fetchWorkshop, clearCurrentWorkshop]);

  // Store return URL in sessionStorage for browser back navigation
  useEffect(() => {
    if (returnUrl && returnUrl !== '/analytics') {
      sessionStorage.setItem('workshopReturnUrl', returnUrl);
    }
  }, [returnUrl]);

  useEffect(() => {
    if (workshop) {
      setProgressInput(workshop.progress_rate?.toString() || '0');
    }
  }, [workshop]);

  // Load users for mentor/mentees display
  const loadUsers = async () => {
    try {
      const fetchedUsers = await UserService.getAllUsers();
      setUsers(fetchedUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsersLoaded(true);
    }
  };

  // Helper function to get user name from ID or return name if already a string
  const getUserName = (userIdOrName: string | number | null): string => {
    if (!userIdOrName) return 'Not assigned';
    const idOrName = userIdOrName.toString();
    
    // If it's a numeric ID, look up the user
    if (/^\d+$/.test(idOrName)) {
      if (!Array.isArray(users) || users.length === 0) {
        return usersLoaded ? idOrName : `Loading... (${idOrName})`;
      }
      const user = users.find(u => u.id === Number(idOrName));
      if (user) {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        return fullName || user.username || idOrName;
      }
      return idOrName;
    }
    
    // If it's already a name, return as is
    return idOrName;
  };

  // Helper function to format mentees array (handle both IDs and names)
  const formatMentees = (mentees: string[] | number[] | null): string => {
    if (!Array.isArray(mentees) || mentees.length === 0) return '';
    
    return mentees
      .map(mentee => getUserName(mentee))
      .filter(name => name && name !== 'Loading...' && name !== 'Not assigned')
      .join(', ');
  };

  const handleProgressUpdate = async () => {
    if (!workshop || !progressInput) return;
    
    try {
      setIsUpdatingProgress(true);
      const progress = parseFloat(progressInput);
      
      if (progress < 0 || progress > 100) {
        alert('Progress must be between 0 and 100');
        return;
      }
      
      await updateProgress(workshop.id, progress);
      setShowProgressUpdate(false);
    } catch (error: any) {
      alert('Failed to update progress: ' + error.message);
    } finally {
      setIsUpdatingProgress(false);
    }
  };

  // Handle workshop deletion
  const handleDeleteWorkshop = () => {
    if (!workshop) return;
    setDeleteAlert({
      isOpen: true,
      workshopId: workshop.id,
      workshopTitle: workshop.title
    });
  };

  // Confirm workshop deletion
  const confirmDeleteWorkshop = async () => {
    if (!deleteAlert.workshopId) return;

    try {
      await WorkshopService.deleteWorkshop(deleteAlert.workshopId);
      // Navigate back to workshops list after successful deletion
      navigate('/workshops');
    } catch (error) {
      console.error('Error deleting workshop:', error);
      // Close the alert even on error (error handling is done by the service/store)
      setDeleteAlert({ isOpen: false, workshopId: null, workshopTitle: '' });
    }
  };

  // Cancel workshop deletion
  const cancelDeleteWorkshop = () => {
    setDeleteAlert({ isOpen: false, workshopId: null, workshopTitle: '' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-medium text-gray-900">Error Loading Workshop</h2>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/workshops')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Back to Workshops
          </button>
        </div>
      </div>
    );
  }

  if (!workshop) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-medium text-gray-900">Workshop not found</h2>
          <button
            onClick={() => navigate('/workshops')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Back to Workshops
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    const baseClasses = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';
    const statusClasses = WorkshopService.getStatusColor(status);
    return `${baseClasses} ${statusClasses}`;
  };

  const deadlineInfo = WorkshopService.getDaysUntilDeadline(workshop.deadline);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <button
                onClick={() => navigate(returnUrl)}
                className="mr-4 text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{workshop.title}</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Workshop #{WorkshopService.formatWorkshopNumber(workshop.workshop_number)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Link
                to={`/workshops/${workshop.id}/kata-board-v2`}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                KATA Board
                {workshop.can_edit === false && (
                  <span className="ml-2 text-xs font-normal text-green-100">View only</span>
                )}
              </Link>
              {isAdmin && (
                <>
                  <button
                    onClick={() => setShowProgressUpdate(!showProgressUpdate)}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Update Progress
                  </button>
                  <Link
                    to={`/workshops/${workshop.id}/edit`}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-700"
                  >
                    Edit Workshop
                  </Link>
                  <button
                    onClick={handleDeleteWorkshop}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-danger hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger"
                    title="Delete Workshop"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Progress Update Panel */}
        {showProgressUpdate && (
          <div className="bg-white shadow rounded-lg mb-6 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Update Progress</h3>
            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <label htmlFor="progress" className="block text-sm font-medium text-gray-700">
                  Progress Rate (%)
                </label>
                <input
                  type="number"
                  id="progress"
                  min="0"
                  max="100"
                  value={progressInput}
                  onChange={(e) => setProgressInput(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleProgressUpdate}
                  disabled={isUpdatingProgress}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {isUpdatingProgress ? 'Updating...' : 'Update'}
                </button>
                <button
                  onClick={() => setShowProgressUpdate(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Workshop Overview</h3>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <span className={getStatusBadge(workshop.status)}>
                        {workshop.status.charAt(0).toUpperCase() + workshop.status.slice(1)}
                      </span>
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Progress</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div
                            className={`h-2 rounded-full ${WorkshopService.getProgressColor(workshop.progress_rate)}`}
                            style={{ width: `${Math.min(workshop.progress_rate || 0, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium">
                          {WorkshopService.formatProgress(workshop.progress_rate)}
                        </span>
                      </div>
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Department</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {workshop.department_name || 'Unassigned'}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Mentor</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {getUserName(workshop.mentor)}
                    </dd>
                  </div>
                  {workshop.mentees && workshop.mentees.length > 0 && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Mentees</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {formatMentees(workshop.mentees)}
                      </dd>
                    </div>
                  )}
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Startup Date</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {WorkshopService.formatDate(workshop.startup_date)}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Deadline</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div>
                        {WorkshopService.formatDate(workshop.deadline)}
                        {deadlineInfo && !['fully_implemented', 'closed', 'rejected'].includes(workshop.status) && (
                          <div className={`text-xs mt-1 ${deadlineInfo.isOverdue ? 'text-red-600' : 'text-gray-500'}`}>
                            {deadlineInfo.isOverdue ? 'Overdue by' : 'Due in'} {deadlineInfo.days} day{deadlineInfo.days !== 1 ? 's' : ''}
                          </div>
                        )}
                        {workshop.status === 'fully_implemented' && (
                          <div className="text-xs mt-1 text-green-600">
                            Fully implemented
                          </div>
                        )}
                      </div>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Product & Location Details */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Product & Location</h3>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  {workshop.product_line && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Product Line</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.product_line}
                      </dd>
                    </div>
                  )}
                  {workshop.alv_id && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">ALV ID</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.alv_id}
                      </dd>
                    </div>
                  )}
                  {workshop.location && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Location</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.location}
                      </dd>
                    </div>
                  )}
                  {workshop.ws_area && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">WS Area</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.ws_area}
                      </dd>
                    </div>
                  )}
                  {workshop.localization && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Localization</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.localization}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {/* Business Classification */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Business Classification</h3>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  {workshop.category && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Category</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.category}
                      </dd>
                    </div>
                  )}
                  {workshop.ebit_bridge_lever && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">EBIT Bridge Lever</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.ebit_bridge_lever}
                      </dd>
                    </div>
                  )}
                  {workshop.impact_type && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Impact Type</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.impact_type}
                      </dd>
                    </div>
                  )}
                  {workshop.workstream && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Workstream</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.workstream}
                      </dd>
                    </div>
                  )}
                  {workshop.l_gate && (
                    <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">L-Gate</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        {workshop.l_gate}
                      </dd>
                    </div>
                  )}
                  {workshop.priority && (
                    <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                      <dt className="text-sm font-medium text-gray-500">Priority</dt>
                      <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          workshop.priority === 'High' ? 'bg-red-100 text-red-800' :
                          workshop.priority === 'Normal' ? 'bg-yellow-100 text-yellow-800' :
                          workshop.priority === 'Low' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {workshop.priority}
                        </span>
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            {/* Description */}
            {workshop.improvement_description && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Description</h3>
                </div>
                <div className="px-4 py-5 sm:px-6">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {workshop.improvement_description}
                  </p>
                </div>
              </div>
            )}

            {/* Conditions */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Conditions</h3>
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Initial Condition</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {workshop.initial_condition ?? 'Not specified'}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Current Condition</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {workshop.current_condition ?? 'Not specified'}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Target Condition</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {workshop.target_condition ?? 'Not specified'}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Financial Impact */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Financial Impact</h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Estimated Savings/Year</dt>
                    <dd className="mt-1 text-lg font-semibold text-gray-900">
                      {WorkshopService.formatCurrencyWithUnit(workshop.estimated_savings_per_year, workshop.estimated_savings_unit)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Released Savings</dt>
                    <dd className="mt-1 text-lg font-semibold text-green-600">
                      {WorkshopService.formatCurrencyWithUnit(workshop.released_savings, workshop.released_savings_unit)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Realization Rate</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {workshop.savings_realization_rate != null ? workshop.savings_realization_rate.toFixed(1) : '0.0'}%
                    </dd>
                  </div>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Categories</h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                <div className="space-y-3">
                  {workshop.sqdcm_category && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">SQDCM Category</dt>
                      <dd className="mt-1 text-sm text-gray-900">{workshop.sqdcm_category}</dd>
                    </div>
                  )}
                  {workshop.aps_maturity && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">APS Maturity</dt>
                      <dd className="mt-1 text-sm text-gray-900">{workshop.aps_maturity}</dd>
                    </div>
                  )}
                  {workshop.kpi && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">KPI</dt>
                      <dd className="mt-1 text-sm text-gray-900">{workshop.kpi}</dd>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Timeline */}
            {(workshop.days_since_startup !== null || workshop.days_until_deadline !== null) && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Timeline</h3>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                  <div className="space-y-3">
                    {workshop.days_since_startup !== null && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Days Since Startup</dt>
                        <dd className="mt-1 text-sm text-gray-900">{workshop.days_since_startup} days</dd>
                      </div>
                    )}
                    {workshop.days_until_deadline !== null && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          {workshop.status === 'fully_implemented' ? 'Deadline Status' : 'Days Until Deadline'}
                        </dt>
                        <dd className={`mt-1 text-sm ${
                          workshop.status === 'fully_implemented' 
                            ? 'text-green-600' 
                            : workshop.days_until_deadline < 0 
                              ? 'text-red-600' 
                              : 'text-gray-900'
                        }`}>
                          {workshop.status === 'fully_implemented' 
                            ? 'Fully implemented' 
                            : workshop.days_until_deadline < 0 
                              ? `Overdue by ${Math.abs(workshop.days_until_deadline)} days`
                              : `${workshop.days_until_deadline} days`
                          }
                        </dd>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Alert */}
      <Alert
        isOpen={deleteAlert.isOpen}
        onClose={cancelDeleteWorkshop}
        onConfirm={confirmDeleteWorkshop}
        type="error"
        title="Delete Workshop"
        message={`Are you sure you want to delete the workshop "${deleteAlert.workshopTitle}"? This action cannot be undone and will also delete all associated KATA Board data.`}
        confirmText="Delete"
        cancelText="Cancel"
        showCancel={true}
      />
    </div>
  );
};

export default WorkshopDetail;
